import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { getLeadSummary } from '@/api/lead_summary'
import {
  assignTeamChatMessage,
  getTeamChatMessages,
  teamChatMessageUrl,
  type TeamChatMessage,
} from '@/api/team-chat'
import { formatMoscowDateTime } from '@/utils/format'

/** Обращения к боту (@avs_carlink_bot …) — вопросы к системе, не переписка по заявке. */
const BOT_MENTION = /@avs_carlink_bot/i

/**
 * «Из чата команды» в карточке лида: ИИ-сводка переписки (только по чату —
 * что обсуждали, что решили, что висит; статус лида — сверху карточки) и
 * свёрнутая лента сообщений TG-группы, привязанных к лиду (точно — по номеру
 * CRM, лоту, отчёту, VIN, госномеру, ответу; или ИИ).
 * «Отвязать» (при наведении) снимает привязку — сообщение пропадает из
 * карточки. Пока сообщений нет — блока нет.
 */
export function TeamChatFeed({ leadId, className }: { leadId: string; className: string }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['team-chat', leadId],
    queryFn: () => getTeamChatMessages(leadId),
    refetchInterval: 60_000,
  })
  const messages = data?.filter((m) => !BOT_MENTION.test(m.text ?? ''))
  const { data: summary } = useQuery({
    queryKey: ['lead-summary', leadId, 'chat'],
    queryFn: () => getLeadSummary(leadId, false, 'chat'),
    enabled: !!messages?.length,
    refetchInterval: 120_000,
  })
  const unlinkMut = useMutation({
    mutationFn: (id: number) => assignTeamChatMessage(id, null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['team-chat', leadId] }),
  })
  if (!messages?.length) return null

  const muted = 'text-xs text-[color:var(--stryzh-color-text-secondary)]'
  return (
    <section className={className}>
      <h2 className="stryzh-heading-card mb-3">{t('teamChat.heading')}</h2>
      {summary ? (
        <div className="mb-3 flex flex-col gap-2">
          <p className="text-sm text-[color:var(--stryzh-color-text-primary)]">{summary.summary.tldr}</p>
          {summary.summary.open?.length ? (
            <div>
              <div className={muted}>{t('teamChat.open')}</div>
              <ul className="ml-4 list-disc text-sm text-[color:var(--stryzh-color-text-primary)]">
                {summary.summary.open.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          ) : null}
          <div className={muted}>{t('teamChat.summaryAt', { at: formatMoscowDateTime(summary.generated_at) })}</div>
        </div>
      ) : null}
      <details>
        <summary className={`cursor-pointer ${muted}`}>{t('teamChat.messages', { n: messages.length })}</summary>
        <div className="mt-3 flex flex-col gap-3">
          {messages.map((m) => (
            <TeamChatItem
              key={m.id}
              m={m}
              actions={
                // На десктопе — только при наведении на сообщение; на телефоне видно всегда.
                <button
                  type="button"
                  className="lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100 lg:focus-visible:opacity-100 hover:text-red-300 disabled:opacity-50"
                  disabled={unlinkMut.isPending}
                  onClick={() => unlinkMut.mutate(m.id)}
                >
                  ✕ {t('teamChat.unlink')}
                </button>
              }
            />
          ))}
        </div>
      </details>
      {unlinkMut.isError ? <div className="mt-2 text-xs text-red-300">{(unlinkMut.error as Error).message}</div> : null}
    </section>
  )
}

/** Одно сообщение чата: автор, время, способ привязки, цитата, вложение, текст. */
export function TeamChatItem({ m, actions }: { m: TeamChatMessage; actions?: ReactNode }) {
  const { t } = useTranslation()
  const muted = 'text-xs text-[color:var(--stryzh-color-text-secondary)]'
  const tgUrl = teamChatMessageUrl(m)
  const replyText = m.reply ? (m.reply.text ?? m.reply.caption ?? '') : ''
  const replyFrom = m.reply?.from ? [m.reply.from.first_name, m.reply.from.last_name].filter(Boolean).join(' ') : ''
  const method = m.link_method
    ? m.link_method === 'ai' && m.link_confidence != null
      ? `${t('teamChat.method.ai')} · ${Math.round(m.link_confidence * 100)}%`
      : t(`teamChat.method.${m.link_method}`)
    : null

  return (
    <div className="group rounded border border-[color:var(--stryzh-color-border-default)] p-3">
      <div className={`mb-1 flex flex-wrap items-center gap-x-2 ${muted}`}>
        <span className="font-medium text-[color:var(--stryzh-color-text-primary)]">{m.from_name ?? '—'}</span>
        <span>{formatMoscowDateTime(m.sent_at)}</span>
        {m.edited_at ? <span>{t('teamChat.edited')}</span> : null}
        {method ? <span>· {method}</span> : null}
        <span className="ml-auto flex items-center gap-2">
          {actions}
          {tgUrl ? (
            <a className="text-[color:var(--stryzh-color-accent-orange)] hover:underline" href={tgUrl} target="_blank" rel="noreferrer">
              {t('teamChat.openTg')}
            </a>
          ) : null}
        </span>
      </div>
      {m.forward_from ? <div className={muted}>{t('teamChat.forwarded', { from: m.forward_from })}</div> : null}
      {replyText ? (
        <div className={`mb-1 border-l-2 border-[color:var(--stryzh-color-border-default)] pl-2 ${muted}`}>
          {replyFrom ? `${replyFrom}: ` : ''}
          {replyText.length > 120 ? `${replyText.slice(0, 120)}…` : replyText}
        </div>
      ) : null}
      {m.media_type ? (
        <div className={muted}>📎 {m.file_name ?? t(`teamChat.media.${m.media_type}`, { defaultValue: m.media_type })}</div>
      ) : null}
      {m.ai_attachment_text ? (
        <div className={`mb-1 italic ${muted}`}>{t('teamChat.attachmentText', { text: m.ai_attachment_text })}</div>
      ) : null}
      {m.text ? (
        <div className="whitespace-pre-wrap break-words text-sm text-[color:var(--stryzh-color-text-primary)]">{m.text}</div>
      ) : null}
    </div>
  )
}
